import React from "react";
import { Container, Graphics, Text } from "@pixi/react";
import { Graphics as PIXIGraphics, TextStyle } from "pixi.js";
import { useCallback, useEffect, useState } from "react";

interface LoadingIndicatorProps {
  x: number;
  y: number;
  scale?: number;
  isVisible: boolean;
  progress?: number; // 0-100 for progress indicator
  text?: string;
  color?: number;
}

const LoadingIndicator: React.FC<LoadingIndicatorProps> = ({
  x,
  y,
  scale = 1,
  isVisible,
  progress,
  text = "Collecting...",
  color = 0x4ade80, // Green color
}) => {
  const [rotation, setRotation] = useState(0);

  // Smooth rotation animation
  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(() => {
      setRotation((prev) => (prev + 0.05) % (Math.PI * 2));
    }, 16); // ~60fps

    return () => clearInterval(interval);
  }, [isVisible]);

  // Draw the spinning ring
  const drawSpinningRing = useCallback((g: PIXIGraphics) => {
    g.clear();
    
    if (!isVisible) return;

    // Outer glow ring
    g.lineStyle(6, color, 0.3);
    g.drawCircle(0, 0, 35);

    // Main spinning ring
    g.lineStyle(4, color, 0.8);
    g.arc(0, 0, 30, rotation, rotation + Math.PI * 1.5);

    // Inner spinning dots
    for (let i = 0; i < 3; i++) {
      const angle = rotation + (i * Math.PI * 0.8);
      const radius = 20;
      const dotX = Math.cos(angle) * radius;
      const dotY = Math.sin(angle) * radius;
      
      g.beginFill(color, 0.9 - i * 0.2);
      g.drawCircle(dotX, dotY, 3 - i);
      g.endFill();
    }

    // Progress ring (if progress is provided)
    if (progress && progress > 0) {
      const progressAngle = (progress / 100) * Math.PI * 2;
      g.lineStyle(3, 0xfbbf24, 0.9); // Amber color for progress
      g.arc(0, 0, 25, -Math.PI / 2, -Math.PI / 2 + progressAngle);
    }
  }, [isVisible, rotation, color, progress]);

  // Draw the pulsing background
  const drawPulsingBackground = useCallback((g: PIXIGraphics) => {
    g.clear();
    
    if (!isVisible) return;

    const pulseScale = 1 + Math.sin(rotation * 3) * 0.2;
    
    // Semi-transparent background circle
    g.beginFill(0x000000, 0.3);
    g.drawCircle(0, 0, 45 * pulseScale);
    g.endFill();

    // Glowing border
    g.lineStyle(2, color, 0.5);
    g.drawCircle(0, 0, 45 * pulseScale);
  }, [isVisible, rotation, color]);

  if (!isVisible) return null;

  const textStyle = new TextStyle({
    fontFamily: 'Arial Black',
    fontSize: 12,
    fill: 0xffffff,
    stroke: 0x000000,
    strokeThickness: 2,
    dropShadow: true,
    dropShadowColor: 0x000000,
    dropShadowBlur: 2,
    dropShadowAngle: Math.PI / 6,
    dropShadowDistance: 1,
  });

  return (
    <Container x={x} y={y} scale={scale}>
      {/* Pulsing background */}
      <Graphics draw={drawPulsingBackground} />
      
      {/* Main spinning indicator */}
      <Graphics draw={drawSpinningRing} />
      
      {/* Collection text */}
      <Text
        text={text}
        style={textStyle}
        anchor={0.5}
        y={55}
      />
      
      {/* Progress text if progress is provided */}
      {progress && progress > 0 && (
        <Text
          text={`${Math.round(progress)}%`}
          style={new TextStyle({
            fontFamily: 'Arial Black',
            fontSize: 10,
            fill: 0xfbbf24,
            stroke: 0x000000,
            strokeThickness: 1,
          })}
          anchor={0.5}
          y={-5}
        />
      )}
    </Container>
  );
};

export default LoadingIndicator; 